import * as React from 'react';
import { Dialog, AppBar, DialogActions, Button, WithStyles, createStyles, Theme, CircularProgress, withStyles, DialogContent, Typography, Toolbar, FormControl, InputLabel, InputBase, Grid, Tooltip, Box, IconButton } from '@material-ui/core';
import { DeltaXMLColors } from './styling/DeltaColors';
import Select from 'react-select';
import { ProfileDetails } from './ProfileContent';
import { LicenseRequestData, LicenseRequestStatus } from './Paperbase';
import InfoIcon from '@material-ui/icons/Info';
import { Utilities } from '../general/utilities';
import { MuiStyles } from './styling/MuiStyles';


export enum EvalStatus {
  NotYetRequested,
  RequestOngoing,
  Success,
  ItemsPopulated,
  Error,
  InternalServerError
}
const BootstrapInput = withStyles((theme: Theme) =>
  createStyles({
    root: {
      'label + &': {
        marginTop: theme.spacing(3),
      },
    },
    input: {
      borderRadius: 4,
      position: 'relative',
      backgroundColor: theme.palette.background.paper,
      border: '1px solid ' + DeltaXMLColors.OXFORD_BLUE,
      fontSize: 16,
      padding: '10px',
      width: '240px',
      '&:focus': {
        // Set border color
        border: '1px solid ' + DeltaXMLColors.CELERY
      }
    },
    error: {
      borderRadius: 4,
      position: 'relative',
      backgroundColor: theme.palette.background.paper,
      border: '1px solid ' + DeltaXMLColors.VALENTINE_RED,
      fontSize: 16,
      width: '262px'
    },
  }),
)(InputBase);
const styles = (theme: Theme) =>
  createStyles({
    margin: {
      margin: theme.spacing(2),
    },
    marginStausMessage: {
      marginLeft: '15px',
      marginTop: '-15px'
    },
    marginProgressCircle: {
      marginTop: '-20px'
    },
    inputLabel: {
      fontSize: '17',
      width: '150px',
      color: DeltaXMLColors.GRAY
    },
    link: {
      color: DeltaXMLColors.FOUNTAIN_BLUE
    },
    error: {
      color: DeltaXMLColors.VALENTINE_RED
    },
    profileLabels: {
      margin: '24px 0px 0px 0px',
      height: '41px',
      width: '240px',
      fontWeight: 500,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      color: DeltaXMLColors.OXFORD_BLUE
    },
    dropDown: {
      margin: '10px 0px 0px 14px',
      height: '41px',
      width: '240px',
    },
    progress: {
      margin: '0px 0px 0px 10px',
      color: DeltaXMLColors.OXFORD_BLUE,
    },
    statusMessage: {
      paddingLeft: '14px',
    },
    info: {
      color: DeltaXMLColors.FOUNTAIN_BLUE,
      marginTop: '-5px',
    }
  });

enum TrialFieldLabel {
  firstName = 'First Name',
  lastName = 'Last Name',
  compName = 'Company Name',
  productName = 'Product Name',

}
export interface EvalData {
  firstName: string,
  lastName: string,
  compName: string,
  productName: string
}

export interface MenuItemFormatType {
  value: string;
  label: string;
};

export interface RequestEvalProps extends WithStyles<typeof styles> {
  open: boolean,
  profileDetails: ProfileDetails,
  evalAllowedProducts: MenuItemFormatType[] | undefined,
  evalRequestStatus: EvalStatus,
  licenseRequestState: LicenseRequestData,
  handleRequestEvalDialogOpen: (open: boolean) => void,
  handleRequestEvaluation: (evalData: EvalData) => void
}

function RequestEvaluation(props: RequestEvalProps) {
  const { classes, open, handleRequestEvalDialogOpen, handleRequestEvaluation, evalRequestStatus, profileDetails, evalAllowedProducts, licenseRequestState } = props;

  const [firstName, setFirstName] = React.useState(profileDetails.givenName);
  const [lastName, setLastName] = React.useState(profileDetails.familyName);
  const [companyName, setCompanyName] = React.useState(profileDetails.compName);
  const [selectedProduct, setSelectedProduct] = React.useState<any>();

  const lineDataRefreshInProgress = licenseRequestState.deals.status !== LicenseRequestStatus.dealsOK && licenseRequestState.deals.status !== LicenseRequestStatus.noItems &&
                                    licenseRequestState.lines.status !== LicenseRequestStatus.dealsOK && licenseRequestState.lines.status !== LicenseRequestStatus.noItems;
  const lineDataRefreshError = licenseRequestState.deals.status === LicenseRequestStatus.errorResponse || licenseRequestState.lines.status === LicenseRequestStatus.errorResponse ||
                               licenseRequestState.systems.status === LicenseRequestStatus.errorResponse;

  const errorInEvalProcess = evalRequestStatus === EvalStatus.Error || evalRequestStatus === EvalStatus.InternalServerError || lineDataRefreshError;

  const ongoingEvalProcess = evalRequestStatus === EvalStatus.RequestOngoing || lineDataRefreshInProgress;

  let requestBarData = "";
  let showProgressCircle = false;
  if (ongoingEvalProcess) {
    showProgressCircle = true;
    requestBarData = "Getting Evaluation data...";
  } else if (errorInEvalProcess) {
    showProgressCircle = false;
    requestBarData = "Error encountered when requesting evaluation data";
  }
  else if (evalRequestStatus === EvalStatus.Success) {
    showProgressCircle = true;
    requestBarData = "Evaluation is ready. You will now be redirected to Evaluation Manager...";
  } 
  requestBarData = requestBarData === '' ? requestBarData : '[' + requestBarData + ']';

  const isProdListPopulated = evalAllowedProducts && evalAllowedProducts.length > 0 && evalRequestStatus === EvalStatus.ItemsPopulated && !lineDataRefreshInProgress;

  const handleTrialConfirm = () => {
    handleRequestEvaluation({
      firstName: firstName,
      lastName: lastName,
      compName: companyName,
      productName: selectedProduct?.value!,
    })
  }

  React.useEffect(() => {
    if (evalRequestStatus === EvalStatus.Success && !lineDataRefreshInProgress) {
      handleRequestEvalDialogOpen(false);
      setSelectedProduct("");
    }
  }, [licenseRequestState]);

  React.useEffect(() => {
    setFirstName(profileDetails.givenName);
    setLastName(profileDetails.familyName);
    setCompanyName(profileDetails.compName);
  }, [profileDetails])

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const fieldValue = event.target.value as string;
    switch (event.target.name) {
      case TrialFieldLabel.firstName:
        setFirstName(fieldValue);
        break;
      case TrialFieldLabel.lastName:
        setLastName(fieldValue);
        break;
      case TrialFieldLabel.compName:
        setCompanyName(fieldValue);
        break;

    }
  }

  const allTextFieldsValid = Utilities.validateNonEmptyText(firstName) && Utilities.validateNonEmptyText(lastName) && Utilities.validateNonEmptyText(companyName) && selectedProduct !== undefined;
  const confirmEvaluationEnabled = allTextFieldsValid && !errorInEvalProcess && !ongoingEvalProcess && evalRequestStatus !== EvalStatus.Success;
  // cancel button is enabled in all cases other than when the redirection to Evaluation Manager is in progress after successful evaluation
  const cancelEvaluationEnabled = evalRequestStatus !== EvalStatus.Success;

  return (
    <Dialog open={open} onClose={() => handleRequestEvalDialogOpen(false)} PaperProps={{ style: { height: '550px' } }}>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h4">Request Evaluation</Typography>
        </Toolbar>
      </AppBar>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormControl className={classes.margin}>
              <InputLabel className={classes.inputLabel} shrink={true}>{TrialFieldLabel.firstName}</InputLabel>
              <BootstrapInput name={TrialFieldLabel.firstName} value={firstName} onChange={handleChange} error={!Utilities.validateNonEmptyText(firstName)} />
            </FormControl>
            <FormControl className={classes.margin}>
              <InputLabel className={classes.inputLabel} shrink={true}>{TrialFieldLabel.lastName}</InputLabel>
              <BootstrapInput name={TrialFieldLabel.lastName} value={lastName} onChange={handleChange} error={!Utilities.validateNonEmptyText(lastName)} />
            </FormControl>
            <FormControl className={classes.margin}>
              <InputLabel className={classes.inputLabel} shrink={true}>{TrialFieldLabel.compName}</InputLabel>
              <BootstrapInput name={TrialFieldLabel.compName} value={companyName} onChange={handleChange} error={!Utilities.validateNonEmptyText(companyName)} />
            </FormControl>
            <FormControl className={classes.margin}>
              <InputLabel className={classes.inputLabel} shrink={true}>Email Address</InputLabel>
              <Typography className={classes.profileLabels}>{profileDetails.emailAddress}</Typography>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl className={classes.margin} >
              <InputLabel className={classes.inputLabel} shrink={true}>
                <Grid container>
                  <Grid item xs={9}>{TrialFieldLabel.productName}</Grid>
                  <Grid item xs={3}>
                    <MuiStyles.HtmlTooltip
                      arrow
                      interactive
                      title={
                        <Typography variant="body2" color="inherit">
                          If this list does not contain the product you are looking for, please contact <a className={classes.link} href="mailto:support@deltaxignia.com?subject=Evaluation product not found">support@deltaxignia.com</a>
                        </Typography>
                      }>
                      <InfoIcon className={classes.info} />
                    </MuiStyles.HtmlTooltip>
                  </Grid>
                </Grid>
              </InputLabel>
            </FormControl>
            <Select className={classes.dropDown} name="product" value={selectedProduct} onChange={product => { setSelectedProduct(product) }} options={evalAllowedProducts} isSearchable={false} isDisabled={!isProdListPopulated} />
          </Grid>
        </Grid>
        <Grid container>
          <Typography className={classes.statusMessage} variant="body2" color="textSecondary">
            {requestBarData}
          </Typography>
          {showProgressCircle &&
            <CircularProgress className={classes.progress} variant="indeterminate" size="20px" />
          }
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="primary" onClick={handleTrialConfirm} disabled={!confirmEvaluationEnabled}>
          Confirm
        </Button>
        <Button variant="contained" color="primary" onClick={() => { handleRequestEvalDialogOpen(false); }} disabled={!cancelEvaluationEnabled}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  )
};

export default withStyles(styles)(RequestEvaluation);